import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormButtonComponent } from './form-button/form-button.component';
import { ToastService } from 'src/app/services/toast.service';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { FormSelectComponent } from '../select/form-select.component';
import { SelectFileComponent } from '../select-file/select-file.component';
import { FormDatePickerComponent } from '../form-date-picker/form-date-picker.component';
import { FormDateRangePickerComponent } from '../form-date-range-picker/form-date-range-picker.component';
import { IFormConfig, IFormItem, ILayout } from 'src/app/shared/interfaces/IForm';
import { FormControlType } from 'src/app/shared/enums';
import { TablerIconsModule } from 'angular-tabler-icons';

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    ReactiveFormsModule,
    FormSelectComponent,
    AutocompleteComponent,
    SelectFileComponent,
    FormDatePickerComponent,
    TranslateModule,
    FormButtonComponent,
    FormDateRangePickerComponent,
    TablerIconsModule
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss'
})
export class DynamicFormComponent implements OnDestroy, OnChanges {
  @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;
  @ViewChildren('dynamicElements') dynamicElements: QueryList<ElementRef>;
  @Input() formConfigs: IFormConfig;
  @Input() formGroup: FormGroup = new FormGroup({});
  @Input() spinLoader: boolean;
  @Input() onSubmit?: () => void;
  unsubscribe: Subscription[] = [];
  controlType = FormControlType;

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private toastService: ToastService) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formGroup']) {
      this.cdr.detectChanges();
    }
  }

  // set error from backend
  set ValidatorError(raw: any) {
    const message = raw?.message ?? raw?.Message;
    const list = Array.isArray(raw) ? raw : raw?.errors;

    let hasFieldErrors = false;

    // 1) BE returns array errors: [{field, error}]
    if (Array.isArray(list)) {
      const grouped: Record<string, string[]> = {};

      list.forEach((e: any) => {
        const field = e?.field;
        const errMsg = e?.error;
        if (!field || !errMsg) return;

        (grouped[field] ||= []).push(errMsg);
      });

      hasFieldErrors = Object.keys(grouped).length > 0;
      if (hasFieldErrors) this.applyFieldErrors(grouped);
    }
    // 2) raw is already a map: { Name: [...] } or { Name: '...' }
    else if (raw && typeof raw === 'object') {
      const maybeMap = raw?.errors ? null : raw;

      if (maybeMap) {
        hasFieldErrors = Object.keys(maybeMap).length > 0;
        if (hasFieldErrors) this.applyFieldErrors(maybeMap);
      }
    }

    // 3) Toast message only when no field errors
    if (message && !hasFieldErrors) {
      this.toastService.error(this.translateService.instant(message));
    }
  }

  private applyFieldErrors(errors: Record<string, any>) {
    Object.keys(errors).forEach((field) => {
      const normalizedField = field.charAt(0).toLowerCase() + field.slice(1);
      const control = this.formGroup.get(normalizedField);
      if (!control) return;

      const val = errors[field];

      const arr = Array.isArray(val) ? val : [val];
      const fieldLowercase = field.toLowerCase();
      const translatedErrors = arr.map((e: string) => {
        if(e.includes(fieldLowercase)) {
          return `${this.translateService.instant(fieldLowercase)} ${this.translateService.instant(e.replace(fieldLowercase, '').trim())}`;
        }
        return this.translateService.instant(e);
      });
      control.setErrors({ message: translatedErrors });
    });
  }

  getErrorMessage(item: IFormItem): string[] {
    const { key, validator, label } = item;
    const control = this.formGroup?.get(key);
    let errorMessage: string[] = [];

    if (control && (control.touched || control.dirty) && control.invalid) {
      // Add error messages from validators
      validator?.forEach((error) => {
        if (control.hasError(error.errorType)) {
          if(label && error.errorMessage.startsWith(label)) { 
            const message = error.errorMessage.replace(label ?? '', '').trim();
            errorMessage.push(`${this.translateService.instant(label ?? '')} ${this.translateService.instant(message, error.params)}`);
          }
          else {
            errorMessage.push(this.translateService.instant(error.errorMessage, error.params ?? {}));
          }
        }
      });

      // Add error messages from backend
      const backendErrors = control.errors?.['message'];
      if (backendErrors) {
        errorMessage = errorMessage.concat(Array.isArray(backendErrors) ? backendErrors : [backendErrors]);
      }
    }

    return errorMessage;
  }

  hasRequired(item: IFormItem) {
    return item.validator?.some((error) => error.errorType === 'required');
  }

  toggleShowPassword(event: Event, input: IFormItem) {
    event.preventDefault();
    input.inputConfig && (input.inputConfig.showingPassword = !input.inputConfig.showingPassword);
  }

  getItemClass({ layoutConfig, itemClass }: any) {
    const layoutClasses =
      layoutConfig?.map((col: ILayout) => (col.screenSize ? `col-${col.screenSize}-${col.column}` : `col-${col.column}`)).join(' ') || '';
    const additionalClass = itemClass ? ` ${itemClass}` : '';
    return layoutClasses === '' ? additionalClass : `${layoutClasses}${additionalClass}`;
  }

  handleEnter(event: Event) {
    event.preventDefault();
    if (!this.formConfigs.preventEnterSubmit) {
      this.submit();
    }
  }

  submit() {
    this.trimFormValues(this.formGroup);
    this.onSubmit?.();
  }

  resetForm() {
    this.formGroupDirective?.resetForm();
  }

  // Trim string values and convert empty/whitespace-only strings to null
  // before the data is submitted, so no leading/trailing spaces or empty
  // strings ever reach the API/DB.
  private trimFormValues(group: FormGroup | FormArray): void {
    Object.keys(group.controls).forEach((key) => {
      const control: AbstractControl | null = group.get(key);
      if (!control) return;

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.trimFormValues(control);
        return;
      }

      if (typeof control.value === 'string') {
        const trimmedValue = control.value.trim();
        control.setValue(trimmedValue === '' ? null : trimmedValue);
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
